import { useOutletContext, useParams } from 'react-router-dom'

import {
  CreateBulkSubprojectsDocument,
  CreateSubprojectDocument,
  ProjectManagementProjectDetailsPageAddSubprojectModalPageDocument,
  SubprojectsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import AddEditSubprojectModal, {
  AddEditSubprojectFormValues,
} from 'modules/project_management/components/modals/AddEditSubprojectModal'
import CreateSubprojectsDialog, {
  CreateSubprojectsFormValues,
} from 'modules/project_management/components/modals/CreateSubprojectsDialog'

const AddSubprojectModalPage = () => {
  const { id } = useParams()
  const projectId = String(id)

  const { hasSubprojectsEntityFeature } = useOutletContext<{
    hasSubprojectsEntityFeature: boolean
  }>()

  const { data, loading: loadingPolicies } = useQuery(
    ProjectManagementProjectDetailsPageAddSubprojectModalPageDocument,
    {
      variables: {
        projectId,
      },
    }
  )

  const canCreateSubproject =
    data?.projectManagement.project?._policies?.subprojectEdit?.can ?? false

  const [createSubproject, { loading }] = useMutation(CreateSubprojectDocument, {
    extractorFn: (mutation) =>
      mutation.projectManagementMutations.createProjectmanagementSubproject,
    refetchQueries: [SubprojectsDataCollectionDocument],
    awaitRefetchQueries: true,
  })

  const [bulkCreateSubprojects, { loading: loadingBulkSubprojects }] = useMutation(
    CreateBulkSubprojectsDocument,
    {
      extractorFn: (mutation) =>
        mutation.projectManagementMutations.bulkCreateProjectmanagementSubproject,
      refetchQueries: [SubprojectsDataCollectionDocument],
      awaitRefetchQueries: true,
    }
  )
  if (loadingPolicies || !canCreateSubproject) return null

  const onSubmit = ({ name }: AddEditSubprojectFormValues) => {
    track(trackingEvents.projectManagement.subprojects.addSubprojectSubmit, {
      project_id: projectId,
    })

    return createSubproject({ variables: { name, projectId } })
  }

  const onSubmitBulkSubprojects = ({ names }: CreateSubprojectsFormValues) => {
    track(trackingEvents.projectManagement.subprojects.addSubprojectSubmit, {
      project_id: projectId,
      subprojectsNumber: names.length,
    })

    return bulkCreateSubprojects({ variables: { names, projectId } })
  }

  return !hasSubprojectsEntityFeature ? (
    <AddEditSubprojectModal
      onSubmit={onSubmit}
      loading={loading}
      title={i18n.t('project_management.modals.create_subproject.title')}
      description={i18n.t('project_management.modals.create_subproject.description')}
      iconName={'document-edit'}
      nameLabel={i18n.t('project_management.modals.create_subproject.input_label')}
      submitButton={i18n.t('project_management.modals.create_subproject.submit')}
    />
  ) : (
    <CreateSubprojectsDialog
      isOpen
      onSubmit={onSubmitBulkSubprojects}
      loading={loadingBulkSubprojects}
    />
  )
}

export default AddSubprojectModalPage
